import React, { useEffect, useState } from 'react'
import { useSelector } from 'react-redux'

import { greyMultibrandPalette } from '@masfront/common-b2c'

import { Box, CircularProgress, makeStyles, ButtonProps } from '@material-ui/core'
import ArrowForwardIcon from '@material-ui/icons/ArrowForward'

import EnergyContractCard from '../components/EnergyContractCard'
import NoEnergyContracts from '../components/NoEnergyContracts'
import useEnergyContracts from '../hooks/useEnergyContract'

import { DocumentType } from '~/common/model'
import { useAppDepsContext } from '~/core/hooks/useAppDepsContext'
import useCustomer from '~/customer/useCustomer'
import { EnergyContract } from '~/energyContract/model/energyContract.model'
import Permission from '~/permissions/model/permissions.model'
import { selectHasPermission } from '~/permissions/store/permissions.selectors'
import { B2CModal } from '~/ui/components'
import GridTemplate3xN from '~/ui/components/GridTemplate3xN/GridTemplate3xN'
import { MainTitleHeader } from '~/ui/components/MainTitleHeader'

interface Action {
  icon?: React.ReactElement
  label: string
  onClick: () => void
  disabled: boolean
}

const useStyles = makeStyles((theme) => ({
  CRMIcon: {
    width: theme.spacing(5),
    height: theme.spacing(5),
  },
}))

const EnergyContractPageContainer: React.FC = () => {
  const classes = useStyles()
  const { runtimeConfig } = useAppDepsContext()
  const { customer } = useCustomer()
  const { fetchEnergyContracts, energyContracts, loading, error } = useEnergyContracts()
  const [isErrorModalOpen, setIsErrorModalOpen] = useState(!!error)
  const documentPrefix = customer?.documentType === DocumentType.PASSPORT ? 'PS' : 'ES'

  const errorModalActions = customer && [
    {
      button: { variant: 'contained', size: 'medium', color: 'secondary' } as ButtonProps,
      name: 'Probar de nuevo',
      onClick: () => {
        fetchEnergyContracts(customer.documentValue)
      },
    },
  ]

  const hasPermissionsAdministrateEnergyContracts = useSelector(
    selectHasPermission(Permission.ADMINISTRATE_ENERGY_CONTRACTS),
  )

  useEffect(() => {
    if (customer) {
      fetchEnergyContracts(customer.documentValue)
    }
  }, [])

  useEffect(() => {
    setIsErrorModalOpen(!!error)
  }, [error])

  let actions: Action[] = []

  if (hasPermissionsAdministrateEnergyContracts) {
    actions = [
      {
        icon: (<ArrowForwardIcon className={classes.CRMIcon} />) as React.ReactElement,
        label: 'Ir a CRM energía',
        onClick: () =>
          window.open(
            `${runtimeConfig.energyHost}/search?q=${documentPrefix}${customer?.documentValue}`,
            '_blank',
          ),
        disabled: false,
      },
    ]
  }

  return (
    <>
      <Box
        bgcolor={greyMultibrandPalette[50]}
        height="100%"
        overflow="auto"
        data-hook="energy-contract-container"
      >
        <MainTitleHeader title="Contrato de Energía" actions={actions} />
        {!loading &&
          !error &&
          energyContracts &&
          (energyContracts.length ? (
            <GridTemplate3xN>
              {energyContracts.map((energyContract: EnergyContract) => (
                <EnergyContractCard key={energyContract.id} energyContract={energyContract} />
              ))}
            </GridTemplate3xN>
          ) : (
            <NoEnergyContracts />
          ))}

        {loading && (
          <Box m={60} display="flex" justifyContent="center">
            <CircularProgress size={100} />
          </Box>
        )}

        {error && (
          <B2CModal
            isOpen={isErrorModalOpen}
            type="error"
            title="Error de carga"
            description="Ha habido un problema y no se han podido cargar los datos de energía. Por favor espera unos instantes y vuelve a probar."
            onClose={() => setIsErrorModalOpen(false)}
            actions={errorModalActions}
          />
        )}
      </Box>
    </>
  )
}

export default EnergyContractPageContainer
